import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Disclosure } from "../../../components/Disclosure";
import { ComunicacionAjax } from "../../../ts/api/comunicacion";
import { Breadcrumb } from "../../../components/Breadcrumb";
import { getSelectionCell, TypedTable } from "../../../components/TypedTable";
import { createColumnHelper, type OnChangeFn } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import Select, { type SingleValue } from "react-select";
import { Loading, QueryError } from "../../../components/Loading/Loading";
import { notificacionTipo } from "../../../components/Alerts";

export function ListadoOPEPage() {
	const [filtroProveedor, setFiltroProveedor] = useState("");
	const [selectedOFs, setSelectedOFs] = useState<Record<string, boolean>>({});
	const queryClient = useQueryClient();


	const onCerrarOFsSeleccionadas = () => {
		const ofsACerrar = Object.entries(selectedOFs).filter(([_, selected]) => selected).map(([id, _]) => id);

		if (ofsACerrar.length === 0) {
			notificacionTipo("warning", "No hay OFs seleccionadas.");
			return;
		}

		const confirmado = window.confirm(`¿Está seguro de que desea archivar ${ofsACerrar.length} OFs de forma permanente?`);
		if (!confirmado) return;

		void ComunicacionAjax.sendAjaxRequest<ResCerrarOPEs>("POST", "/api/v1/cerrarOPEs", {
			idOPEs: ofsACerrar,
		}).then((res) => {
			if (res.status == "exito") notificacionTipo("success", res.message);
			else notificacionTipo("error", res.message);

			return queryClient.invalidateQueries({ queryKey: ["getLineasOPE"] });

		});
	};



	return (
		<div id="content" className="content" >
			<title>Almacen | Listado de OPEs</title>
			{/*Content*/}
			<Breadcrumb
				sectionTitle="Listado de OPEs"
				links={[
					{ label: "Listado de OPEs", active: true }
				]}
			/>


			<Disclosure style={{ marginBottom: 20 }}>
				{/* First element is the summary, second is the content */}
				<h4 className="panel-title panel-heading senalar">
					Filtro
				</h4>
				<FiltroProveedores
					proveedor={filtroProveedor}
					setProveedor={setFiltroProveedor}
				/>
			</Disclosure>


			<h1>Líneas de OPE abiertas</h1>
			<div className="table-responsive">
				<TablaOPEs
					filtroProveedor={filtroProveedor}
					selected={selectedOFs}
					setSelected={setSelectedOFs}
				/>
			</div>

			<div className="contenedorBotones">
				<button className="btnPrimary" onClick={onCerrarOFsSeleccionadas} type="button">
					Archivar OPEs seleccionadas
				</button>
			</div>
		</div>
	);
};

function FiltroProveedores(props: { readonly proveedor: string; readonly setProveedor: (value: string) => void; }) {
	const { data, error, isPending } = UseProveedores("OPE");

	if (error) return <QueryError error={error} fallback="Error al cargar los proveedores" />;
	if (isPending) return <Loading />;

	const options = data.map(prov => ({ value: prov.idProveedor, label: `${prov.idProveedor} - ${prov.nombre}` }));

	const value = options.find(opt => opt.value === props.proveedor);
	const setValue = (newValue: SingleValue<{ value: string; label: string; }>) => {
		props.setProveedor(newValue?.value ?? "");
	};

	return (
		<div className="contenedorFiltros" role="tabpanel">
			<label style={{ fontSize: 17 }}>
				Proveedores:
			</label>
			<br />
			<Select
				options={options}
				className=""
				value={value}
				onChange={setValue}

				isClearable
			/>
		</div>
	);
}

interface TablaOPEsProps {
	readonly filtroProveedor: string;
	readonly selected: Record<string, boolean>;
	readonly setSelected: OnChangeFn<Record<string, boolean>>;
}
function TablaOPEs(props: TablaOPEsProps) {
	const { data, error, isPending } = UseOPEs();

	const columns = useMemo(() => {
		const columnHelper = createColumnHelper<LineaOPEPendiente>();

		const idAccesor = (linea: LineaOPEPendiente) => `#${String(linea.idOPE).padStart(3, "0")} - ${linea.idLineaOPE}`;
		const conjuntoAccesor = (linea: LineaOPEPendiente) => {
			return linea.loteConjunto != "0" ?
				`${linea.idConjunto} - ${linea.descConjunto} - ${linea.loteConjunto}` :
				`${linea.idConjunto} - ${linea.descConjunto}`;
		};
		const percentFormatter = new Intl.NumberFormat("es-ES", {
			style: "percent",
			minimumFractionDigits: 0,
			maximumFractionDigits: 2
		});
		return [
			getSelectionCell(columnHelper),
			columnHelper.accessor(idAccesor, { header: "ID" }),
			columnHelper.accessor("nombreProveedor", { header: "Proveedor" }),
			columnHelper.accessor(conjuntoAccesor, { id: "conjunto", header: "Conjunto" }),
			columnHelper.accessor("cantidadFabricar", { header: "Fabricar" }),
			columnHelper.accessor("cantidadRecibida", { header: "Recibida" }),
			columnHelper.accessor("percentEnviada", { header: "Enviada", cell: info => percentFormatter.format(info.getValue()) }),
		];
	}, []);

	const filteredData = useMemo(() => {
		if (!data) return [];
		if (!props.filtroProveedor) return data;
		return data.filter(linea => linea.idProveedor == props.filtroProveedor);
	}, [data, props.filtroProveedor]);


	if (error) return <QueryError error={error} fallback="Error al cargar las líneas de OPE" />;
	if (isPending) return <Loading />;

	return (
		<TypedTable
			className="dataTable display"
			data={filteredData}
			columns={columns}

			selection={props.selected}
			getRowId={row => `${row.idOPE}_${row.idLineaOPE}`}
			setSelection={props.setSelected}
		/>
	);
}


function UseOPEs() {
	return useQuery<LineaOPEPendiente[]>({
		queryKey: ["getLineasOPE"],
		queryFn: () => {
			const url = "/api/v1/getLineasOPE";
			return ComunicacionAjax.sendAjaxRequest<LineaOPEPendiente[]>("GET", url);
		},
	});
}
function UseProveedores(tipo: string) {
	return useQuery<Proveedor[]>({
		queryKey: ["getProveedores", tipo],
		queryFn: () => {
			const url = "/getProveedores";
			return ComunicacionAjax.sendAjaxRequest<Proveedor[]>("GET", url, { tipo });
		},
	});
}
interface Proveedor {
	idProveedor: string,
	nombre: string;
}
interface LineaOPEPendiente {
	idProveedor: string;
	nombreProveedor: string;

	idOPE: number;
	idLineaOPE: number;

	idOF: number | null;
	idFaseOF: number | null;

	idConjunto: string;
	descConjunto: string;
	loteConjunto: string;

	cantidadFabricar: number;
	cantidadRecibida: number;
	percentEnviada: number;
}

interface ResCerrarOPEs {
	status: string;
	message: string;
}